import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Calendar as CalIcon, Loader2, ShieldCheck } from "lucide-react";
import { SERVICE_META } from "@/data/services";
import { SearchableSelect } from "@/components/SearchableSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Service", "Date & Time", "Details", "Deposit"];

const CURRENT_YEAR = new Date().getFullYear() + 1;
const YEARS = Array.from({ length: CURRENT_YEAR - 1989 }, (_, i) => String(CURRENT_YEAR - i));

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

// Opening hours by weekday (0=Sun ... 6=Sat): [openHour, closeHour] in 24h
const HOURS = {
  0: [8, 21],  // Sunday
  1: [8, 22], // Monday
  2: [8, 22], // Tuesday
  3: [8, 22], // Wednesday
  4: [8, 22], // Thursday
  5: [8, 22], // Friday
  6: [8, 21],  // Saturday
};

function formatHour(h) {
  const period = h >= 12 ? "PM" : "AM";
  let hr = h % 12;
  if (hr === 0) hr = 12;
  return `${hr}:00 ${period}`;
}

function getSlotsForDate(dateStr, blockedHours = []) {
  if (!dateStr) return [];
  const [y, m, d] = dateStr.split("-").map(Number);
  const dateObj = new Date(y, m - 1, d);
  const day = dateObj.getDay();
  const [open, close] = HOURS[day];
  const now = new Date();
  const isToday = dateObj.toDateString() === now.toDateString();
  const cutoff = now.getTime() + 2 * 60 * 60 * 1000; // 2-hour booking buffer
  const blocked = new Set(blockedHours);
  const slots = [];
  for (let h = open; h < close; h += 1) {
    if (isToday && new Date(y, m - 1, d, h, 0, 0, 0).getTime() < cutoff) continue;
    if (blocked.has(h)) continue; // 1-hour buffer around existing bookings
    slots.push(formatHour(h));
  }
  return slots;
}

function minDate() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function Book() {
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [blockedHours, setBlockedHours] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [couponInput, setCouponInput] = useState("");

  const [form, setForm] = useState({
    service_id: "",
    date: "",
    time_slot: "",
    customer_first: "",
    customer_last: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    city: "",
    state: "NC",
    zip: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_mileage: "",
    vin: "",
    brake_options: [],
    engine_cylinder: "",
    engine_hemi: "",
    oil_type: "standard",
    service_addons: [],
    coupon: "",
    notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => {
      setServices(r.data);
      const pre = params.get("service");
      if (pre && r.data.find((s) => s.id === pre)) {
        setForm((f) => ({ ...f, service_id: pre }));
      }
    }).catch(() => {});
  }, [params]);

  // Fetch travel-surcharge quote when entering the review step
  useEffect(() => {
    if (step !== 3 || !form.service_id) return;
    let cancelled = false;
    setQuoteLoading(true);
    setQuote(null);
    axios
      .post(`${API}/quote`, {
        service_id: form.service_id,
        brake_options: form.brake_options,
        engine_cylinder: form.engine_cylinder,
        engine_hemi: form.engine_hemi,
        oil_type: form.oil_type,
        service_addons: form.service_addons,
        coupon: form.coupon,
        address: [form.address, form.city, form.state, form.zip].filter(Boolean).join(", "),
        zip: form.zip,
      })
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) setQuote(null); })
      .finally(() => { if (!cancelled) setQuoteLoading(false); });
    return () => { cancelled = true; };
  }, [step, form.service_id, form.brake_options, form.engine_cylinder, form.engine_hemi, form.oil_type, form.service_addons, form.coupon, form.address, form.city, form.state, form.zip]);

  // Load vehicle makes once
  useEffect(() => {
    setMakesLoading(true);
    axios.get(`${API}/vehicle/makes`)
      .then((r) => setMakes(r.data || []))
      .catch(() => setMakes([]))
      .finally(() => setMakesLoading(false));
  }, []);

  // Load models whenever make + year are both chosen
  useEffect(() => {
    if (!form.vehicle_make || !form.vehicle_year) { setModels([]); return; }
    let cancelled = false;
    setModelsLoading(true);
    setModels([]);
    axios.get(`${API}/vehicle/models/${encodeURIComponent(form.vehicle_make)}/${form.vehicle_year}`)
      .then((r) => { if (!cancelled) setModels(r.data || []); })
      .catch(() => { if (!cancelled) setModels([]); })
      .finally(() => { if (!cancelled) setModelsLoading(false); });
    return () => { cancelled = true; };
  }, [form.vehicle_make, form.vehicle_year]);

  // Load booked/blocked hours whenever a date is chosen (1-hour buffer enforcement)
  useEffect(() => {
    if (!form.date) { setBlockedHours([]); return; }
    let cancelled = false;
    axios.get(`${API}/availability/${form.date}`)
      .then((r) => { if (!cancelled) setBlockedHours(r.data?.blocked_hours || []); })
      .catch(() => { if (!cancelled) setBlockedHours([]); });
    return () => { cancelled = true; };
  }, [form.date]);

  const selected = services.find((s) => s.id === form.service_id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setYear = (v) => setForm((f) => ({ ...f, vehicle_year: v, vehicle_model: "" }));
  const setMake = (v) => setForm((f) => ({ ...f, vehicle_make: v, vehicle_model: "" }));
  const setModel = (v) => setForm((f) => ({ ...f, vehicle_model: v }));
  const setDate = (v) => setForm((f) => ({ ...f, date: v, time_slot: "" }));
  const fullAddress = [form.address, form.city, form.state, form.zip].filter(Boolean).join(", ");
  const slots = getSlotsForDate(form.date, blockedHours);
  const isBrake = form.service_id === "brake-service";
  const isEngine = form.service_id === "engine-tune-up";
  const isOil = form.service_id === "oil-change";

  const setService = (id) =>
    setForm((f) => ({
      ...f,
      service_id: id,
      brake_options: id === "brake-service" ? f.brake_options : [],
      engine_cylinder: id === "engine-tune-up" ? f.engine_cylinder : "",
      engine_hemi: id === "engine-tune-up" ? f.engine_hemi : "",
      oil_type: id === "oil-change" ? (f.oil_type || "standard") : f.oil_type,
      service_addons: [],
    }));

  const toggleServiceAddon = (key) =>
    setForm((f) => ({
      ...f,
      service_addons: f.service_addons.includes(key)
        ? f.service_addons.filter((k) => k !== key)
        : [...f.service_addons, key],
    }));

  const setEngineCylinder = (key) =>
    setForm((f) => ({ ...f, engine_cylinder: key, engine_hemi: key === "8cyl" ? f.engine_hemi : "" }));

  const toggleBrake = (key) =>
    setForm((f) => {
      const has = f.brake_options.includes(key);
      let next = has ? f.brake_options.filter((k) => k !== key) : [...f.brake_options, key];
      if (has && key === "front_brakes") next = next.filter((k) => k !== "front_rotors");
      if (has && key === "rear_brakes") next = next.filter((k) => k !== "rear_rotors");
      return { ...f, brake_options: next };
    });

  // Effective pricing — compute the service base, then apply universal add-ons
  const pricing = (() => {
    if (!selected) return { price: 0, deposit: 0, label: "", savings: 0 };
    let base = selected.price;
    let label = selected.name;
    let baseSavings = 0;
    let deposit = selected.deposit;

    if (isBrake) {
      const optMap = Object.fromEntries((selected.options || []).map((o) => [o.key, o]));
      const sel = form.brake_options.filter((k) => optMap[k]);
      const individual = sel.reduce((a, k) => a + optMap[k].price, 0);
      let price = 0;
      const remaining = new Set(sel);
      const bundles = [...(selected.bundles || [])].sort((a, b) => b.keys.length - a.keys.length);
      for (const b of bundles) {
        if (b.keys.every((k) => remaining.has(k))) {
          price += b.price;
          b.keys.forEach((k) => remaining.delete(k));
        }
      }
      remaining.forEach((k) => { price += optMap[k].price; });
      base = price;
      label = sel.map((k) => optMap[k].label).join(", ");
      baseSavings = individual - price;
    } else if (isEngine) {
      const cyl = (selected.cylinder_options || []).find((o) => o.key === form.engine_cylinder);
      const isHemi = form.engine_cylinder === "8cyl" && form.engine_hemi === "yes";
      base = isHemi ? (selected.hemi_price || 0) : (cyl ? cyl.price : 0);
      label = isHemi ? "8-Cylinder HEMI" : (cyl?.label || "");
    } else if (isOil) {
      const oilMap = Object.fromEntries((selected.oil_options || []).map((o) => [o.key, o]));
      const ot = oilMap[form.oil_type] || oilMap["standard"];
      base = ot ? ot.price : 0;
      label = ot?.label || "";
    }

    // Universal add-ons (each with its own bundle saving)
    const addonMap = Object.fromEntries((selected.addons || []).map((a) => [a.key, a]));
    const sel = (form.service_addons || []).filter((k) => addonMap[k]);
    const addAmt = sel.reduce((a, k) => a + (addonMap[k].price - addonMap[k].discount), 0);
    const addSavings = sel.reduce((a, k) => a + addonMap[k].discount, 0);
    const addLabel = sel.map((k) => addonMap[k].label).join(", ");
    const fullLabel = [label, addLabel].filter(Boolean).join(" + ");
    return { price: base + addAmt, deposit, label: fullLabel, savings: baseSavings + addSavings };
  })();

  const canProceed = () => {
    if (step === 0) {
      if (!form.service_id) return false;
      if (isBrake) return form.brake_options.includes("front_brakes") || form.brake_options.includes("rear_brakes");
      if (isEngine) return !!form.engine_cylinder && (form.engine_cylinder !== "8cyl" || !!form.engine_hemi);
      if (isOil) return !!form.oil_type;
      return true;
    }
    if (step === 1) return !!form.date && !!form.time_slot;
    if (step === 2)
      return (
        form.customer_first && form.customer_last && /\S+@\S+\.\S+/.test(form.customer_email) && form.customer_phone &&
        form.address && form.city && form.state && form.zip && form.vehicle_make && form.vehicle_model &&
        form.vehicle_year && form.vehicle_mileage && form.vin
      );
    return true;
  };

  const next = () => {
    if (!canProceed()) {
      setShowErrors(true);
      toast.error("Please complete the required fields highlighted in red.");
      return;
    }
    setShowErrors(false);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setShowErrors(false); setStep((s) => Math.max(s - 1, 0)); };

  // Invalid-field flags (only shown after a failed Continue attempt)
  const emailBad = !/\S+@\S+\.\S+/.test(form.customer_email);
  const inv = {
    customer_first: showErrors && !form.customer_first,
    customer_last: showErrors && !form.customer_last,
    customer_email: showErrors && emailBad,
    customer_phone: showErrors && !form.customer_phone,
    address: showErrors && !form.address,
    city: showErrors && !form.city,
    state: showErrors && !form.state,
    zip: showErrors && !form.zip,
    vehicle_year: showErrors && !form.vehicle_year,
    vehicle_make: showErrors && !form.vehicle_make,
    vehicle_model: showErrors && !form.vehicle_model,
    vehicle_mileage: showErrors && !form.vehicle_mileage,
    vin: showErrors && !form.vin,
    date: showErrors && !form.date,
    time_slot: showErrors && !form.time_slot,
  };
  const errCls = (bad) => `${inputCls} ${bad ? "border-red-500 ring-1 ring-red-500" : ""}`;

  const payDeposit = async () => {
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const booking = { ...form, address: fullAddress, customer_name: `${form.customer_first} ${form.customer_last}`.trim() };
      const { data } = await axios.post(`${API}/checkout/session`, { booking, origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(e.response.data?.detail || "That time was just taken. Please pick another slot.");
        setBlockedHours([]);
        setForm((f) => ({ ...f, time_slot: "" }));
        setStep(1);
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-neutral-900 border border-neutral-800 rounded-sm px-4 py-3 text-white placeholder:text-neutral-600 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 outline-none transition-colors";

  return (
    <main data-testid="booking-page" className="pt-32 pb-24 min-h-screen">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tighter text-white mb-2">
          Book Your Service
        </h1>
        <p className="text-neutral-400 mb-2">Lock in your slot with a quick deposit. The rest is paid after the job.</p>
        <p className="text-sm text-yellow-400/90 mb-10" data-testid="parts-labor-note">All listed prices include parts and labor.</p>

        {/* STEP INDICATOR */}
        <div className="flex items-center mb-12">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-2" data-testid={`step-indicator-${i}`}>
                <div
                  className={`w-9 h-9 rounded-sm flex items-center justify-center text-sm font-bold transition-colors ${
                    i <= step ? "bg-yellow-400 text-black" : "bg-neutral-900 border border-neutral-800 text-neutral-500"
                  }`}
                >
                  {i < step ? <Check className="w-5 h-5" /> : i + 1}
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold hidden sm:block ${i <= step ? "text-yellow-400" : "text-neutral-600"}`}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-px flex-1 mx-2 ${i < step ? "bg-yellow-400" : "bg-neutral-800"}`} />
              )}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* STEP 0 — SERVICE */}
            {step === 0 && (
              <div className="space-y-6" data-testid="step-service">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {services.map((s) => {
                    const Icon = (SERVICE_META[s.id] || {}).icon;
                    const active = form.service_id === s.id;
                    const hasOptions = s.id === "brake-service" || s.id === "engine-tune-up" || s.id === "oil-change";
                    return (
                      <button
                        key={s.id}
                        onClick={() => setService(s.id)}
                        data-testid={`select-service-${s.id}`}
                        className={`text-left p-6 rounded-sm border transition-colors flex flex-col ${
                          active ? "border-yellow-400 bg-yellow-400/5" : "border-neutral-800 bg-[#141414] hover:border-neutral-600"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          {Icon && (
                            <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${active ? "bg-yellow-400" : "bg-neutral-900 border border-neutral-800"}`}>
                              <Icon className={`w-5 h-5 ${active ? "text-black" : "text-yellow-400"}`} />
                            </div>
                          )}
                          {active && <Check className="w-5 h-5 text-yellow-400" />}
                        </div>
                        <h3 className="font-heading font-bold text-lg text-white">{s.name}</h3>
                        <p className="text-sm text-neutral-400 mt-1 mb-3">{s.tagline}</p>
                        {s.description && (
                          <p className="text-sm text-neutral-400 leading-relaxed mb-3 max-w-2xl">{s.description}</p>
                        )}
                        <div className="flex items-center gap-3 text-sm mt-auto pt-2">
                          <span className="font-mono text-white">{hasOptions ? "From " : ""}${s.price.toFixed(0)}</span>
                          <span className="text-yellow-400 font-mono">${s.deposit.toFixed(0)} deposit</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Brake multi-select */}
                {isBrake && (
                  <div className="bg-[#141414] border border-neutral-800 rounded-sm p-6" data-testid="brake-options">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-1">Choose your brake work <span className="text-red-400">*</span></h4>
                    <p className="text-sm text-neutral-500 mb-4">Select front and/or rear brakes (at least one required). We only use premium Gold Ceramic brake pads. Pricing is per axle — PROMOTION: Save $49 by servicing both the front & rear brakes, and $69 with the full brake service package!</p>
                    <div className="space-y-3">
                      {[
                        { brakeKey: "front_brakes", rotorKey: "front_rotors", axle: "Front" },
                        { brakeKey: "rear_brakes", rotorKey: "rear_rotors", axle: "Rear" },
                      ].map(({ brakeKey, rotorKey, axle }) => {
                        const brakeOpt = (selected.options || []).find((o) => o.key === brakeKey);
                        const rotorOpt = (selected.options || []).find((o) => o.key === rotorKey);
                        const brakeChecked = form.brake_options.includes(brakeKey);
                        const rotorChecked = form.brake_options.includes(rotorKey);
                        return (
                          <div key={brakeKey} className="rounded-sm border border-neutral-800 bg-neutral-900/40 p-4">
                            <button
                              onClick={() => toggleBrake(brakeKey)}
                              data-testid={`brake-option-${brakeKey}`}
                              className={`w-full flex items-center justify-between p-3 rounded-sm border transition-colors ${
                                brakeChecked ? "border-yellow-400 bg-yellow-400/5" : "border-neutral-800 bg-neutral-900 hover:border-neutral-600"
                              }`}
                            >
                              <span className="flex items-center gap-3">
                                <span className={`w-5 h-5 rounded-sm border flex items-center justify-center ${brakeChecked ? "bg-yellow-400 border-yellow-400" : "border-neutral-600"}`}>
                                  {brakeChecked && <Check className="w-4 h-4 text-black" />}
                                </span>
                                <span className="font-medium text-white">{brakeOpt?.label}</span>
                              </span>
                              <span className="font-mono text-sm text-neutral-300">${brakeOpt?.price.toFixed(0)}</span>
                            </button>
                            {brakeChecked && (
                              <button
                                onClick={() => toggleBrake(rotorKey)}
                                data-testid={`brake-option-${rotorKey}`}
                                className={`mt-3 ml-6 w-[calc(100%-1.5rem)] flex items-center justify-between p-3 rounded-sm border transition-colors ${
                                  rotorChecked ? "border-yellow-400 bg-yellow-400/5" : "border-neutral-800 bg-neutral-900 hover:border-neutral-600"
                                }`}
                              >
                                <span className="flex items-center gap-3">
                                  <span className={`w-5 h-5 rounded-sm border flex items-center justify-center ${rotorChecked ? "bg-yellow-400 border-yellow-400" : "border-neutral-600"}`}>
                                    {rotorChecked && <Check className="w-4 h-4 text-black" />}
                                  </span>
                                  <span className="font-medium text-white">Add {axle} Rotors</span>
                                </span>
                                <span className="font-mono text-sm text-neutral-300">+${rotorOpt?.price.toFixed(0)}</span>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Oil Change — required oil type */}
                {isOil && (
                  <div className="bg-[#141414] border border-neutral-800 rounded-sm p-6" data-testid="oil-options">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-1">Oil type <span className="text-red-400">*</span></h4>
                    <p className="text-sm text-neutral-500 mb-4">Covers up to 5 quarts — the oil filter is included. Extra quarts are $10/qt.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(selected.oil_options || []).map((o) => {
                        const checked = form.oil_type === o.key;
                        return (
                          <button
                            key={o.key}
                            onClick={() => set("oil_type", o.key)}
                            data-testid={`oil-type-${o.key}`}
                            className={`flex items-center justify-between p-4 rounded-sm border transition-colors ${
                              checked ? "border-yellow-400 bg-yellow-400/5" : "border-neutral-800 bg-neutral-900 hover:border-neutral-600"
                            }`}
                          >
                            <span className="flex items-center gap-3">
                              <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${checked ? "bg-yellow-400 border-yellow-400" : "border-neutral-600"}`}>
                                {checked && <Check className="w-3.5 h-3.5 text-black" />}
                              </span>
                              <span className="font-medium text-white">{o.label}</span>
                            </span>
                            <span className="font-mono text-sm text-neutral-300">${o.price.toFixed(0)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Engine Tune Up — required cylinder + optional filter add-ons */}
                {isEngine && (
                  <div className="space-y-4" data-testid="engine-options">
                    <div className="bg-[#141414] border border-neutral-800 rounded-sm p-6">
                      <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-1">Engine size <span className="text-red-400">*</span></h4>
                      <p className="text-sm text-neutral-500 mb-4">Required — we only use premium OEM or NGK Laser Iridium spark plugs. If additional ignition coils are needed, your final bill increases by $50 per coil.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {(selected.cylinder_options || []).map((o) => {
                          const checked = form.engine_cylinder === o.key;
                          return (
                            <button
                              key={o.key}
                              onClick={() => setEngineCylinder(o.key)}
                              data-testid={`engine-cylinder-${o.key}`}
                              className={`flex items-center justify-between p-4 rounded-sm border transition-colors ${
                                checked ? "border-yellow-400 bg-yellow-400/5" : "border-neutral-800 bg-neutral-900 hover:border-neutral-600"
                              }`}
                            >
                              <span className="flex items-center gap-3">
                                <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${checked ? "bg-yellow-400 border-yellow-400" : "border-neutral-600"}`}>
                                  {checked && <Check className="w-3.5 h-3.5 text-black" />}
                                </span>
                                <span className="font-medium text-white">{o.label}</span>
                              </span>
                              <span className="font-mono text-sm text-neutral-300">${o.price.toFixed(0)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {form.engine_cylinder === "8cyl" && (
                      <div className="bg-[#141414] border border-neutral-800 rounded-sm p-6" data-testid="hemi-section">
                        <label className="text-sm font-medium text-white block mb-3">Do you have a Dodge / Chrysler HEMI engine? <span className="text-red-400">*</span></label>
                        <select
                          value={form.engine_hemi}
                          onChange={(e) => set("engine_hemi", e.target.value)}
                          data-testid="select-hemi"
                          className={`${inputCls} max-w-sm [color-scheme:dark] ${showErrors && !form.engine_hemi ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        >
                          <option value="">Select…</option>
                          <option value="no">No — standard 8-cylinder</option>
                          <option value="yes">Yes — Dodge / Chrysler HEMI</option>
                        </select>
                        <p className="mt-3 text-xs text-amber-400/90" data-testid="hemi-note">
                          HEMI engines require 16 spark plugs and are priced accordingly — HEMI spark plug service is $759.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Universal add-ons — offered on every service */}
                {selected && (
                  <div className="bg-[#141414] border border-neutral-800 rounded-sm p-6" data-testid="service-addons">
                    {(selected.addons || []).length > 0 && (
                      <>
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-1">Add-ons <span className="text-neutral-500 normal-case tracking-normal">(optional)</span></h4>
                        <p className="text-sm text-neutral-500 mb-4">Add additional services and save on each one!</p>
                        <div className="grid grid-cols-1 gap-3">
                          {(selected.addons || []).map((a) => {
                            const checked = form.service_addons.includes(a.key);
                            return (
                              <button
                                key={a.key}
                                onClick={() => toggleServiceAddon(a.key)}
                                data-testid={`service-addon-${a.key}`}
                                className={`flex items-center justify-between p-4 rounded-sm border transition-colors ${
                                  checked ? "border-yellow-400 bg-yellow-400/5" : "border-neutral-800 bg-neutral-900 hover:border-neutral-600"
                                }`}
                              >
                                <span className="flex items-center gap-3">
                                  <span className={`w-5 h-5 rounded-sm border flex items-center justify-center ${checked ? "bg-yellow-400 border-yellow-400" : "border-neutral-600"}`}>
                                    {checked && <Check className="w-4 h-4 text-black" />}
                                  </span>
                                  <span className="font-medium text-white">{a.label}</span>
                                </span>
                                <span className="font-mono text-sm text-neutral-300">
                                  +${(a.price - a.discount).toFixed(0)}{a.discount > 0 && <span className="text-neutral-600 line-through ml-1">${a.price.toFixed(0)}</span>}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                    <div className="mt-4 border-t border-neutral-800 pt-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-neutral-400">{pricing.label || "Select your options"}</span>
                        <span className="font-heading font-bold text-2xl text-yellow-400" data-testid="service-total">${pricing.price.toFixed(0)}</span>
                      </div>
                      {pricing.savings > 0 && (
                        <div className="flex items-center justify-end" data-testid="service-savings">
                          <span className="text-xs font-bold uppercase tracking-wider text-green-400 bg-green-400/10 border border-green-400/30 rounded-sm px-2 py-1">
                            Bundle saves ${pricing.savings.toFixed(0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 1 — DATE & TIME */}
            {step === 1 && (
              <div className="space-y-8" data-testid="step-datetime">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 flex items-center gap-2 mb-3">
                    <CalIcon className="w-4 h-4" /> Choose a date
                  </label>
                  <input
                    type="date"
                    min={minDate()}
                    value={form.date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="booking-date"
                    className={`${inputCls} max-w-xs [color-scheme:dark] ${inv.date ? "border-red-500 ring-1 ring-red-500" : ""}`}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 block mb-3">Pick a time slot</label>
                  {!form.date ? (
                    <p className={`text-sm ${inv.date ? "text-red-400" : "text-neutral-500"}`} data-testid="slots-empty">Select a date first to see available times.</p>
                  ) : slots.length === 0 ? (
                    <p className="text-sm text-amber-400/80" data-testid="no-slots">No available times for this date — bookings require at least 2 hours' notice. Please choose another day.</p>
                  ) : (
                    <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 ${inv.time_slot ? "ring-1 ring-red-500 rounded-sm p-2" : ""}`}>
                      {slots.map((t) => (
                        <button
                          key={t}
                          onClick={() => set("time_slot", t)}
                          data-testid={`time-slot-${t.replace(/[:\s]/g, "")}`}
                          className={`py-3 rounded-sm border text-sm font-medium transition-colors ${
                            form.time_slot === t ? "border-yellow-400 bg-yellow-400 text-black" : "border-neutral-800 bg-[#141414] text-neutral-300 hover:border-neutral-600"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2 — DETAILS */}
            {step === 2 && (
              <div className="space-y-8" data-testid="step-details">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-4">Your Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input className={errCls(inv.customer_first)} placeholder="First name" value={form.customer_first} onChange={(e) => set("customer_first", e.target.value)} data-testid="input-first-name" />
                    <input className={errCls(inv.customer_last)} placeholder="Last name" value={form.customer_last} onChange={(e) => set("customer_last", e.target.value)} data-testid="input-last-name" />
                    <input className={errCls(inv.customer_email)} placeholder="Email address" type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="input-email" />
                    <input className={errCls(inv.customer_phone)} placeholder="Phone number" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="input-phone" />
                    <input className={errCls(inv.address)} placeholder="Street address" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="input-address" />
                    <input className={errCls(inv.city)} placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="input-city" />
                    <SearchableSelect
                      testId="select-state"
                      value={form.state}
                      onChange={(v) => set("state", v)}
                      options={US_STATES}
                      placeholder="State"
                      searchPlaceholder="Search state…"
                      error={inv.state}
                    />
                    <input className={errCls(inv.zip)} placeholder="Zip code" value={form.zip} onChange={(e) => set("zip", e.target.value)} data-testid="input-zip" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-4">Your Vehicle</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <SearchableSelect
                      testId="select-year"
                      value={form.vehicle_year}
                      onChange={setYear}
                      options={YEARS}
                      placeholder="Year"
                      searchPlaceholder="Search year…"
                      error={inv.vehicle_year}
                    />
                    <SearchableSelect
                      testId="select-make"
                      value={form.vehicle_make}
                      onChange={setMake}
                      options={makes}
                      loading={makesLoading}
                      placeholder="Make"
                      searchPlaceholder="Search make…"
                      emptyText="No makes found."
                      error={inv.vehicle_make}
                    />
                    <SearchableSelect
                      testId="select-model"
                      value={form.vehicle_model}
                      onChange={setModel}
                      options={models}
                      loading={modelsLoading}
                      disabled={!form.vehicle_year || !form.vehicle_make}
                      placeholder={!form.vehicle_year || !form.vehicle_make ? "Select year & make first" : "Model"}
                      searchPlaceholder="Search model…"
                      emptyText="No models for this year."
                      error={inv.vehicle_model}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <input className={errCls(inv.vehicle_mileage)} type="number" min="0" placeholder="Mileage (e.g. 84000)" value={form.vehicle_mileage} onChange={(e) => set("vehicle_mileage", e.target.value)} data-testid="input-mileage" />
                    <input
                      className={`${errCls(inv.vin)} font-mono uppercase tracking-wider`}
                      maxLength={17}
                      placeholder="VIN/Plate Number"
                      value={form.vin}
                      onChange={(e) => set("vin", e.target.value.toUpperCase())}
                      data-testid="input-vin"
                    />
                  </div>
                  <textarea className={`${inputCls} mt-4 resize-none`} rows={3} placeholder="Anything we should know? (optional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-notes" />
                  <p className="mt-3 text-xs text-neutral-500 leading-relaxed" data-testid="surcharge-disclaimer">
                    Additional surcharges may apply based on your vehicle's make &amp; model. Travel beyond a 15-mile radius
                    of ZIP 28269 is $1 per additional mile, calculated from your service address. The travel fee is added to
                    the final bill after service. Only your deposit is charged now.
                  </p>
                </div>
              </div>
            )}

            {/* STEP 3 — DEPOSIT / REVIEW */}
            {step === 3 && selected && (
              <div className="space-y-6" data-testid="step-deposit">
                <button
                  onClick={back}
                  data-testid="review-back-button"
                  className="inline-flex items-center gap-2 text-neutral-400 hover:text-white font-medium uppercase text-sm tracking-wide transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Edit details
                </button>
                <div className="bg-[#141414] border border-neutral-800 rounded-sm p-7">
                  <h3 className="font-heading font-bold text-xl text-white mb-6">Booking Summary</h3>
                  <dl className="space-y-3 text-sm">
                    <Row label="Service" value={isBrake ? `Brake Service — ${pricing.label}` : isEngine ? `Spark Plugs — ${pricing.label}` : isOil ? `Oil Change — ${pricing.label}` : pricing.label} />
                    <Row label="When" value={`${form.date} · ${form.time_slot}`} />
                    <Row label="Vehicle" value={`${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model} · ${Number(form.vehicle_mileage).toLocaleString()} mi`} />
                    <Row label="VIN" value={form.vin} />
                    <Row label="Location" value={fullAddress} />
                    <Row label="Contact" value={`${form.customer_first} ${form.customer_last} · ${form.customer_phone}`} />
                    <div className="border-t border-neutral-800 my-3" />
                    <div className="pb-1" data-testid="coupon-section">
                      {quote && quote.coupon_status === "applied" ? (
                        <div className="flex items-center justify-between gap-3 bg-green-400/10 border border-green-400/30 rounded-sm px-3 py-2">
                          <span className="text-sm text-green-400 font-semibold" data-testid="coupon-applied-label">{form.coupon} applied</span>
                          <button type="button" onClick={() => { set("coupon", ""); setCouponInput(""); }} data-testid="coupon-remove-button" className="text-xs uppercase tracking-wide text-neutral-400 hover:text-white transition-colors">Remove</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value)}
                            placeholder="Coupon code"
                            data-testid="coupon-input"
                            className="flex-1 bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 outline-none uppercase"
                          />
                          <button type="button" onClick={() => set("coupon", couponInput.trim())} data-testid="coupon-apply-button" className="px-4 py-2 rounded-sm bg-neutral-800 text-white text-sm font-bold uppercase tracking-wide hover:bg-neutral-700 transition-colors">Apply</button>
                        </div>
                      )}
                      {quote && quote.coupon_status === "invalid" && (
                        <p className="mt-2 text-xs text-red-400" data-testid="coupon-error">That coupon code isn't valid.</p>
                      )}
                      {quote && quote.coupon_status === "wrong_service" && (
                        <p className="mt-2 text-xs text-amber-400/90" data-testid="coupon-wrong-service">This coupon only applies to an Oil Change.</p>
                      )}
                    </div>
                    {pricing.savings > 0 && (
                      <div className="flex justify-between gap-4" data-testid="summary-savings">
                        <dt className="text-green-400">Bundle savings</dt>
                        <dd className="text-green-400 text-right font-semibold">− ${pricing.savings.toFixed(2)}</dd>
                      </div>
                    )}
                    <div className="flex justify-between gap-4" data-testid="summary-price-row">
                      <dt className="text-neutral-500">Service price <span className="text-neutral-600">(parts &amp; labor)</span></dt>
                      <dd className="text-white text-right">{`$${pricing.price.toFixed(2)}`}</dd>
                    </div>
                    {quote && quote.coupon_status === "applied" && quote.coupon_discount > 0 && (
                      <div className="flex justify-between gap-4" data-testid="summary-coupon">
                        <dt className="text-green-400">Coupon {form.coupon} <span className="block text-xs text-neutral-500">{quote.coupon_label}</span></dt>
                        <dd className="text-green-400 text-right font-semibold">− ${quote.coupon_discount.toFixed(2)}</dd>
                      </div>
                    )}
                    {quoteLoading && (
                      <div className="flex justify-between gap-4 text-neutral-400" data-testid="summary-surcharge-loading">
                        <dt>Travel fee (est.)</dt>
                        <dd className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculating…</dd>
                      </div>
                    )}
                    {quote && quote.travel_surcharge > 0 && (
                      <div className="flex justify-between gap-4" data-testid="summary-surcharge">
                        <dt className="text-neutral-300">
                          Travel fee (est.)
                          <span className="block text-xs text-neutral-500">{quote.distance_miles} mi from ZIP {quote.base_zip} · ${quote.per_mile}/mi past {quote.free_radius_miles} · billed after service, not now</span>
                        </dt>
                        <dd className="text-neutral-400 text-right font-semibold">+ ${quote.travel_surcharge.toFixed(2)}</dd>
                      </div>
                    )}
                    {quote && !quote.located && (
                      <p className="text-xs text-amber-400/80" data-testid="surcharge-unverified">
                        We couldn't verify your address distance — any travel fee will be confirmed before service.
                      </p>
                    )}
                    <div className="flex justify-between items-center pt-2">
                      <dt className="text-yellow-400 font-bold uppercase tracking-wide text-xs">Deposit charged now</dt>
                      <dd className="font-heading font-extrabold text-3xl text-yellow-400" data-testid="deposit-due">${(quote ? quote.charged_now : pricing.deposit).toFixed(2)}</dd>
                    </div>
                    <p className="text-xs text-neutral-500">
                      Only the deposit is charged today. The remaining service balance
                      {quote && quote.travel_surcharge > 0 ? <> plus the ${quote.travel_surcharge.toFixed(2)} travel fee</> : null} is paid after the job is complete.
                    </p>
                  </dl>
                </div>

                <div className="flex items-start gap-3 text-sm text-neutral-400 bg-neutral-900/50 border border-neutral-800 rounded-sm p-4">
                  <ShieldCheck className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                  <span>Only your deposit is charged now via Stripe and applied to your final bill. Any travel fee shown above is an estimate billed after service — it is not charged at checkout.</span>
                </div>

                <button
                  onClick={payDeposit}
                  disabled={submitting || quoteLoading}
                  data-testid="pay-deposit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-4 rounded-sm hover:bg-yellow-500 transition-colors disabled:opacity-60"
                >
                  {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</> : quoteLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Calculating total…</> : <>Pay ${(quote ? quote.charged_now : pricing.deposit).toFixed(2)} Deposit <ArrowRight className="w-5 h-5" /></>}
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* NAV BUTTONS */}
        {step < 3 && (
          <div className="flex items-center justify-between mt-10">
            <button
              onClick={step === 0 ? () => navigate("/") : back}
              data-testid="booking-back-button"
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white font-medium uppercase text-sm tracking-wide transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              onClick={next}
              data-testid="booking-next-button"
              className="inline-flex items-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-3 rounded-sm hover:bg-yellow-500 transition-colors"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-white text-right">{value}</dd>
    </div>
  );
}
